import { Config } from 'config';

import { forEach, map } from 'lodash';

import { OwnedObject, invalidateOwner } from 'gear';

import { Xml } from '../util';
import { Point } from '../geometry';
import { Color } from './color';

import { rgb2lab, rgb2xyz, rgb2lch, rgb2hsl,
         lch2rgb, lab2rgb, hsl2rgb,
         interpolate_rgb, interpolate_hsl,
         interpolate_lab, interpolate_lch } from './color-space';

import { ColorShiftEffect } from './glEffects/colorShift';

const toImageData = function(image) {
  let context;
  if( image instanceof HTMLImageElement ) {
    const canvas = document.createElement('canvas');
    canvas.width  = image.width ;
    canvas.height = image.height;
    context = canvas.getContext('2d');
    context.drawImage(image,0,0);
  }
  else {
    context = image.getContext('2d');
  }
  return context.getImageData(0,0,image.width,image.height);
};

const toCanvas = function(imageData) {
  const canvas = document.createElement('canvas');
  canvas.width  = imageData.width ;
  canvas.height = imageData.height;
  canvas.getContext('2d').putImageData(imageData,0,0);
  return canvas;
};

const _floor = Math.floor, _min = Math.min, _max = Math.max, _sqrt = Math.sqrt;

const interpolate_value = function(l1,l2,a) {
  return l1 + ( l2 - l1 ) * a;
};

// values between 0...255
// map :: function(r,g,b,a){ return [ r*, g*, b*, a* ]; }

const modifyImageDataRGBA = function(imageData,map){
  const data = imageData.data, width = imageData.width, height = imageData.height;
  let y, x, inpos, outpos, r, g, b, a;

  for(y = 0; y < height; ++y) {
    inpos = y * width * 4; // *4 for 4 ints per pixel
    outpos = inpos;
    for(x = 0; x < width; ++x) {

      r = data[inpos++];
      g = data[inpos++];
      b = data[inpos++];
      a = data[inpos++]; // alpha

      // http://www.w3.org/TR/AERT#color-contrast
      const c = map(r,g,b,a);

      data[outpos++] = c[0];
      data[outpos++] = c[1];
      data[outpos++] = c[2];
      data[outpos++] = c[3];
    }
  }
};

// values between 0...255
// map :( r,g,b) { return [ r*, g*, b* ]; }

const modifyImageDataRGB = function(imageData,map) {
  const data = imageData.data, width = imageData.width, height = imageData.height;
  let y, x, inpos, outpos, r, g, b;

  for(y = 0; y < height; ++y) {
    inpos = y * width * 4; // *4 for 4 ints per pixel
    outpos = inpos;
    for(x = 0; x < width; ++x) {

      r = data[inpos++];
      g = data[inpos++];
      b = data[inpos++];

      ++inpos; // alpha

      // http://www.w3.org/TR/AERT#color-contrast
      const c = map(r,g,b);

      data[outpos++] = c[0];
      data[outpos++] = c[1];
      data[outpos++] = c[2];

      ++outpos; // alpha
    }
  }
};

// HSLShift
//======================================================================================
const hslShift = function(image, hShift, sShift, lShift) {
    const imageData = toImageData(image);

    modifyImageDataRGB( imageData, (r,g,b) => {
      const c = rgb2hsl([r,g,b]);
      c[0]+= hShift;
      while(c[0] > 360) {c[0]-=360;}
      while(c[0] < 0)   {c[0]+=360;}

      c[1]+=sShift*100;
      if (c[1]<0) c[1]=0;
      else if (c[1]>100) c[1]=100;

      c[2]+=lShift*100;
      if (c[2]<0) c[2]=0;
      else if (c[2]>100) c[2]=100;
      
      return hsl2rgb(c);
    });

    return toCanvas(imageData);
}

// HSLShift
//======================================================================================
let mGlCanvas = null;
let mGlCtx = null;
let mEffect = null;
export const hslShift_gl = function(image, hShift, sShift, lShift) {

  if (mGlCanvas == null)
  {
    mGlCanvas = document.createElement('canvas');
    mGlCtx = mGlCanvas.getContext('webgl', { premultipliedAlpha: false });

    if (mGlCtx != null)
    {
      mGlCtx.pixelStorei(mGlCtx.UNPACK_ALIGNMENT, 1);
      mEffect = new ColorShiftEffect(mGlCtx);

      //pass in clipspace coordiantes to vertex shader
      let lPositionsBuffer = mGlCtx.createBuffer();
      mGlCtx.bindBuffer(mGlCtx.ARRAY_BUFFER, lPositionsBuffer);
      mGlCtx.bufferData(
        mGlCtx.ARRAY_BUFFER,
        new Float32Array([
          -1.0, -1.0,
          1.0, -1.0,
          -1.0,  1.0,
          -1.0,  1.0,
          1.0, -1.0,
          1.0,  1.0
        ]),
        mGlCtx.STATIC_DRAW);

      //pass in texture coordinates to vertext shader
      let lTexCoordBuffer = mGlCtx.createBuffer();
      mGlCtx.bindBuffer(mGlCtx.ARRAY_BUFFER, lTexCoordBuffer);
      mGlCtx.bufferData(mGlCtx.ARRAY_BUFFER,
        new Float32Array([
          0.0, 0.0,
          1.0, 0.0,
          0.0, 1.0,
          0.0, 1.0,
          1.0, 0.0,
          1.0, 1.0
        ]), mGlCtx.STATIC_DRAW);


      //pass in clipspace coordiantes to vertex shader
      mGlCtx.bindBuffer(mGlCtx.ARRAY_BUFFER, lPositionsBuffer);
      mGlCtx.enableVertexAttribArray(mEffect.a_position);
      mGlCtx.vertexAttribPointer(mEffect.a_position, 2, mGlCtx.FLOAT, false, 0, 0);

      //pass in texture coordinates to vertext shader
      mGlCtx.bindBuffer(mGlCtx.ARRAY_BUFFER, lTexCoordBuffer);
      mGlCtx.enableVertexAttribArray(mEffect.a_texCoord);
      mGlCtx.vertexAttribPointer(mEffect.a_texCoord, 2, mGlCtx.FLOAT, false, 0, 0);
    }
  }

  if (mGlCtx == null) {
    return hslShift(image, hShift, sShift, lShift); //Fallback to slow implementation
  }

  mGlCanvas.width = image.width;
  mGlCanvas.height = image.height;
  mGlCtx.viewport(0, 0, mGlCanvas.width, mGlCanvas.height);

  let texture = mGlCtx.createTexture();
  mGlCtx.activeTexture(mGlCtx.TEXTURE0);
  mGlCtx.bindTexture(mGlCtx.TEXTURE_2D, texture);
  mGlCtx.texImage2D(mGlCtx.TEXTURE_2D, 0, mGlCtx.RGBA, mGlCtx.RGBA, mGlCtx.UNSIGNED_BYTE, image);
  if (mGlCtx.getError() != mGlCtx.NO_ERROR) {
    return hslShift(image, hShift, sShift, lShift); //Fallback to slow implementation
  }
  mGlCtx.texParameteri(mGlCtx.TEXTURE_2D, mGlCtx.TEXTURE_WRAP_S, mGlCtx.CLAMP_TO_EDGE);
  mGlCtx.texParameteri(mGlCtx.TEXTURE_2D, mGlCtx.TEXTURE_WRAP_T, mGlCtx.CLAMP_TO_EDGE);
  mGlCtx.texParameteri(mGlCtx.TEXTURE_2D, mGlCtx.TEXTURE_MIN_FILTER, mGlCtx.NEAREST);
  mGlCtx.texParameteri(mGlCtx.TEXTURE_2D, mGlCtx.TEXTURE_MAG_FILTER, mGlCtx.NEAREST);

  mEffect.update(hShift, sShift, lShift);
  mGlCtx.drawArrays(mGlCtx.TRIANGLES, 0, 6);

  return mGlCanvas;
}



/** WEBPACK FOOTER **
 ** ./src/cdl/style/hsl-shift.js
 **/